import { Box, Flex, Grid, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';

import { CalendarTimeIcon, DiscordIcon, DurationIcon, Image, MicOnIcon, PeopleIcon } from '#features/ui';
import { PhoneFrame } from './PhoneFrame';

const CYCLE_MS = 15500;
const GAME_MS = 2600; // game picker -> details
const CREATE_MS = 7400; // details -> post
const APEX = '#fe634e';
const TEAM_LIMIT = 4;

type GameInfo = { icon?: string | null; iconColor?: string | null; image?: string | null; name?: string | null; slug?: string | null };

const zeroPad = (n: number) => String(n).padStart(2, '0');
const fmt = (t: number) => `${Math.floor(t / 3600) ? `${Math.floor(t / 3600)}:` : ''}${zeroPad(Math.floor((t % 3600) / 60))}:${zeroPad(t % 60)}`;

const useTicker = (startFrom: number) => {
  const [s, setS] = useState(startFrom);
  useEffect(() => {
    const id = setInterval(() => setS((v) => (v <= 1 ? startFrom : v - 1)), 1000);
    return () => clearInterval(id);
  }, [startFrom]);
  return s;
};

const CheckSvg = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GameIcon = ({ game, color, size = 18 }: { game?: GameInfo; color: string; size?: number }) =>
  game?.icon ? (
    <Box w={`${size}px`} h={`${size}px`} flexShrink={0} bgColor={color} style={{ mask: `url(${game.icon}) center / contain no-repeat`, WebkitMask: `url(${game.icon}) center / contain no-repeat` }} />
  ) : (
    <Box w={`${size}px`} h={`${size}px`} borderRadius="4px" flexShrink={0} style={{ background: `linear-gradient(135deg, ${color} 0%, #7C3AED 130%)` }} />
  );

const Tag = ({ children }: { children: React.ReactNode }) => (
  <Flex alignItems="center" px={2} py={1} borderRadius="md" bgColor="bg.emphasized" fontSize="11px" fontWeight="600" color="fg.emphasized" whiteSpace="nowrap">
    {children}
  </Flex>
);

const Label = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="9px" fontWeight="700" color="fg.muted" textTransform="uppercase" letterSpacing="0.07em" mb={1}>
    {children}
  </Text>
);

const Pill = ({ active, children }: { active?: boolean; children: React.ReactNode }) => (
  <Flex alignItems="center" gap={1} px={2} py={1} borderRadius="lg" fontSize="11px" fontWeight="700" whiteSpace="nowrap" transition="all 0.3s" bg={active ? 'purple.500' : 'whiteAlpha.100'} color={active ? 'white' : 'fg.emphasized'} border="1px solid" borderColor={active ? 'purple.400' : 'whiteAlpha.100'} w="fit-content">
    {children}
  </Flex>
);

/** A select field that starts empty and gets its value "flown in". */
const SelectField = ({ label, value, filled, delay, dir }: { label: string; value: React.ReactNode; filled: boolean; delay: number; dir: number }) => (
  <Box asChild>
    <m.div initial={{ opacity: 0, x: dir * 24, y: 12 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay, duration: 0.5, ease: 'backOut' }}>
      <Box>
        <Label>{label}</Label>
        <Flex position="relative" overflow="hidden" alignItems="center" justifyContent="space-between" px={1.5} minH="27px" borderRadius="lg" transition="background 0.3s, border-color 0.3s" bg={filled ? 'purple.500' : 'whiteAlpha.50'} border="1px solid" borderColor={filled ? 'purple.400' : 'whiteAlpha.100'}>
          {filled ? (
            <Flex alignItems="center" gap={1} fontSize="10px" fontWeight="700" color="white" whiteSpace="nowrap" asChild>
              <m.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'backOut' }}>
                {value}
              </m.div>
            </Flex>
          ) : (
            <>
              <Text fontSize="11px" color="fg.subtle">
                Select
              </Text>
              <Text fontSize="9px" color="fg.muted">
                ▾
              </Text>
            </>
          )}
          {filled && (
            <Box position="absolute" inset={0} pointerEvents="none" asChild>
              <m.div initial={{ opacity: 0.7 }} animate={{ opacity: 0 }} transition={{ duration: 0.6 }} style={{ background: 'radial-gradient(circle at 50% 50%, rgba(160,150,255,0.6), transparent 70%)' }} />
            </Box>
          )}
        </Flex>
      </Box>
    </m.div>
  </Box>
);

const Avatar = ({ color, initial, size = 24, delay, border = 'hsl(228,11.6%,10%)' }: { color: string; initial: string; size?: number; delay?: number; border?: string }) => (
  <Flex w={`${size}px`} h={`${size}px`} borderRadius="full" alignItems="center" justifyContent="center" fontSize={`${Math.round(size * 0.42)}px`} fontWeight="800" color="white" flexShrink={0} border="2px solid" borderColor={border} style={{ background: `linear-gradient(135deg, ${color} 0%, #8E7BFF 135%)` }} asChild>
    <m.div initial={{ opacity: delay != null ? 0 : 1, scale: delay != null ? 0.3 : 1 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: delay ?? 0, duration: 0.4, ease: 'backOut' }}>
      {initial}
    </m.div>
  </Flex>
);

const MRow = ({ children, delay, dir = 1 }: { children: React.ReactNode; delay: number; dir?: number }) => (
  <Box asChild>
    <m.div initial={{ opacity: 0, x: dir * 30, y: 16 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ delay, duration: 0.5, ease: 'backOut' }}>
      {children}
    </m.div>
  </Box>
);

/* ---------- Phase 1: create post ---------- */
const SchedOpt = ({ label, icon, children, delay }: { label: string; icon: React.ReactNode; children: React.ReactNode; delay: number }) => (
  <Box flex={1} asChild>
    <m.div initial={{ opacity: 0, y: 10, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay, duration: 0.4, ease: 'backOut' }}>
      <Box>
        <Text fontSize="8px" fontWeight="700" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" textAlign="center" mb={1}>
          {label}
        </Text>
        <Flex alignItems="center" justifyContent="center" gap={1} py={1.5} borderRadius="lg" bg="whiteAlpha.100" border="1px solid" borderColor="purple.400" fontSize="11px" fontWeight="700" color="fg.emphasized" whiteSpace="nowrap">
          <Box color="purple.300" fontSize="12px">
            {icon}
          </Box>
          {children}
        </Flex>
      </Box>
    </m.div>
  </Box>
);

/* ---------- Phase 0: pick a game ---------- */
const GameScreen = ({ games, featuredSlug }: { games: GameInfo[]; featuredSlug: string }) => {
  const [picked, setPicked] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setPicked(true), 1500);
    return () => clearTimeout(id);
  }, []);

  return (
    <Flex direction="column" h="100%" pt="34px" px={3.5} pb={3.5} gap={2.5}>
      <MRow delay={0.05} dir={1}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize="15px" fontWeight="800" color="fg.emphasized">
            New post
          </Text>
          <Flex w="22px" h="22px" borderRadius="full" bg="whiteAlpha.100" alignItems="center" justifyContent="center" color="fg.muted" fontSize="13px">
            ✕
          </Flex>
        </Flex>
      </MRow>
      <MRow delay={0.15} dir={-1}>
        <Text fontSize="10px" fontWeight="700" color="fg.muted" textTransform="uppercase" letterSpacing="0.08em">
          Choose your game
        </Text>
      </MRow>

      <Grid templateColumns="1fr 1fr" gap={2}>
        {games.slice(0, 10).map((g, i) => {
          const active = picked && g.slug === featuredSlug;
          return (
            <Box key={g.slug ?? i} asChild>
              <m.div initial={{ opacity: 0, scale: 0.85, y: 14 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.08, duration: 0.4, ease: 'backOut' }}>
                <Flex direction="column" alignItems="center" justifyContent="center" gap={1.5} py={2.5} borderRadius="xl" position="relative" transition="all 0.3s" bg={active ? 'purple.500' : 'whiteAlpha.50'} border="1px solid" borderColor={active ? 'purple.400' : 'whiteAlpha.100'}>
                  <GameIcon game={g} color={active ? '#ffffff' : g.iconColor || '#8E7BFF'} size={26} />
                  <Text
                    fontSize="9px"
                    fontWeight="700"
                    color={active ? 'white' : 'fg.emphasized'}
                    textAlign="center"
                    lineHeight="1.15"
                    minH="21px"
                    maxW="100%"
                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {g.name}
                  </Text>
                  {active && (
                    <Flex position="absolute" top="5px" right="5px" w="14px" h="14px" borderRadius="full" bg="white" color="purple.500" alignItems="center" justifyContent="center" asChild>
                      <m.div initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, ease: 'backOut' }}>
                        <CheckSvg size={9} />
                      </m.div>
                    </Flex>
                  )}
                </Flex>
              </m.div>
            </Box>
          );
        })}
      </Grid>
    </Flex>
  );
};

/* ---------- Phase 1: details ---------- */
const CreateScreen = ({ game, color }: { game?: GameInfo; color: string }) => {
  const [mode, setMode] = useState<'now' | 'scheduled'>('now');
  const [voicePicked, setVoicePicked] = useState(false);
  const [filled, setFilled] = useState(0);

  useEffect(() => {
    // fields appear empty, then values fly in one by one, then session + voice mode
    const fills = [1000, 1250, 1500, 1750, 2000].map((d, i) => setTimeout(() => setFilled((f) => Math.max(f, i + 1)), d));
    const s = setTimeout(() => setMode('scheduled'), 2650);
    const d = setTimeout(() => setVoicePicked(true), 3650);
    return () => {
      fills.forEach(clearTimeout);
      clearTimeout(s);
      clearTimeout(d);
    };
  }, []);

  return (
    <Flex direction="column" h="100%" pt="34px" px={3.5} pb={3.5} gap="7px">
      <MRow delay={0.05} dir={1}>
        <Flex alignItems="center" gap={2} px={2} py={1} borderRadius="lg" bg="whiteAlpha.50" border="1px solid" borderColor="whiteAlpha.100">
          <GameIcon game={game} color={color} size={16} />
          <Text fontSize="12px" fontWeight="700" color="fg.emphasized" flex="1">
            {game?.name ?? 'Apex Legends'}
          </Text>
          <Flex alignItems="center" gap={1} color="#22c55e" fontSize="10px" fontWeight="700">
            <CheckSvg size={10} />
            selected
          </Flex>
        </Flex>
      </MRow>

      <MRow delay={0.18} dir={1}>
        <Box>
          <Label>Platform</Label>
          <HStack gap={1.5}>
            <Pill active={filled >= 1}>PC</Pill>
            <Pill>PlayStation</Pill>
            <Pill>Xbox</Pill>
          </HStack>
        </Box>
      </MRow>

      <Grid templateColumns="1fr 1fr" gap={2}>
        <SelectField label="Rank" value="Diamond" filled={filled >= 2} delay={0.42} dir={-1} />
        <SelectField label="Skill" value="🎮 Experienced" filled={filled >= 3} delay={0.5} dir={1} />
        <SelectField label="Game mode" value="🎖️ Ranked" filled={filled >= 4} delay={0.58} dir={-1} />
        <SelectField label="Region" value="🌍 Europe" filled={filled >= 5} delay={0.66} dir={1} />
      </Grid>

      <MRow delay={0.8} dir={-1}>
        <Box>
          <Label>Session</Label>
          <Flex bg="whiteAlpha.50" borderRadius="lg" p="3px" gap="3px" border="1px solid" borderColor="whiteAlpha.100">
            {(['now', 'scheduled'] as const).map((k) => (
              <Flex key={k} flex={1} justifyContent="center" py={1.5} borderRadius="md" fontSize="11px" fontWeight="700" transition="all 0.25s" bg={mode === k ? 'purple.500' : 'transparent'} color={mode === k ? 'white' : 'fg.muted'}>
                {k === 'now' ? '⚡ Play now' : '📅 Scheduled'}
              </Flex>
            ))}
          </Flex>
        </Box>
      </MRow>

      {/* Scheduled adds when/players/duration (selectable) */}
      <AnimatePresence initial={false}>
        {mode === 'scheduled' && (
          <Box key="sched" asChild>
            <m.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35 }}>
              <HStack gap={1.5} pt="1px" pb="1px" alignItems="flex-end">
                <SchedOpt label="When" icon={<CalendarTimeIcon />} delay={0.15}>
                  in 1h
                </SchedOpt>
                <SchedOpt label="Players" icon={<PeopleIcon />} delay={0.3}>
                  4
                </SchedOpt>
                <SchedOpt label="Length" icon={<DurationIcon />} delay={0.45}>
                  2h
                </SchedOpt>
              </HStack>
            </m.div>
          </Box>
        )}
      </AnimatePresence>

      {/* Voice - a choice, like the real VoiceModePicker: Teamplay voice first
          (picked here), Discord second. Both stay available. */}
      <MRow delay={0.92} dir={1}>
        <Flex gap={1.5}>
          <Flex
            flex={1}
            alignItems="center"
            gap={1.5}
            px={2}
            py={1.5}
            borderRadius="lg"
            transition="all 0.3s"
            bg={voicePicked ? 'rgba(75,80,230,0.18)' : 'whiteAlpha.50'}
            border="1px solid"
            borderColor={voicePicked ? 'purple.400' : 'whiteAlpha.100'}
          >
            <Box color={voicePicked ? 'purple.300' : 'fg.muted'} fontSize="13px">
              <MicOnIcon />
            </Box>
            <Text
              fontSize="11px"
              fontWeight="700"
              color={voicePicked ? 'fg.emphasized' : 'fg.muted'}
              whiteSpace="nowrap"
            >
              Teamplay voice
            </Text>
          </Flex>
          <Flex
            alignItems="center"
            gap={1.5}
            px={2}
            py={1.5}
            borderRadius="lg"
            bg="whiteAlpha.50"
            border="1px solid"
            borderColor="whiteAlpha.100"
            color="fg.muted"
          >
            <Box fontSize="13px">
              <DiscordIcon />
            </Box>
            <Text fontSize="11px" fontWeight="700" whiteSpace="nowrap">
              Discord
            </Text>
          </Flex>
        </Flex>
      </MRow>

      <Box flex="1" />

      <Box asChild>
        <m.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0, scale: [1, 1, 0.96, 1] }} transition={{ opacity: { delay: 1.05, duration: 0.4 }, y: { delay: 1.05, duration: 0.4 }, scale: { delay: 4.6, duration: 0.5, times: [0, 0.5, 0.7, 1] } }}>
          <Flex w="100%" py={2.5} borderRadius="xl" bg="purple.500" color="white" fontSize="13px" fontWeight="800" alignItems="center" justifyContent="center" style={{ boxShadow: '0 0 20px rgba(75,80,230,0.5)' }}>
            Publish
          </Flex>
        </m.div>
      </Box>
    </Flex>
  );
};

/* ---------- Phase 2: the scheduled post page ---------- */
// Skill (not per-game rank) keeps the recommended list universal across every game - and
// English-only, since the mock is a fixed illustration on both locales.
const RECOMMENDED = [
  { id: 'nova', initial: 'N', color: '#f59e0b', name: 'Nova', meta: 'Experienced · EU', joinAt: 2200 },
  { id: 'ash', initial: 'A', color: '#38bdf8', name: 'Ash', meta: 'Competitive · EU', joinAt: 3800 },
  { id: 'zoe', initial: 'Z', color: '#ec4899', name: 'Zoe', meta: 'Chill · EU' },
];

const StartsInRow = () => {
  const t = useTicker(3598); // ~1 hour, counting down to session start
  return (
    <Flex alignItems="center" gap={2} px={2.5} py={1.5} borderRadius="lg" style={{ background: 'linear-gradient(135deg, rgba(75,80,230,0.28) 0%, rgba(126,123,255,0.18) 100%)' }}>
      <Box color="purple.200" fontSize="14px">
        <CalendarTimeIcon />
      </Box>
      <Text fontSize="11px" color="fg.muted" fontWeight="600">
        Starts in
      </Text>
      <Text fontSize="12px" fontWeight="800" color="fg.emphasized" fontVariantNumeric="tabular-nums">
        {fmt(t)}
      </Text>
      <Flex alignItems="center" gap={1} ml="auto" color="fg.muted" fontSize="11px" fontWeight="600">
        <Box fontSize="12px">
          <DurationIcon />
        </Box>
        2h
      </Flex>
    </Flex>
  );
};

const SessionBlock = () => {
  const [invited, setInvited] = useState<string[]>([]);

  useEffect(() => {
    const timers = RECOMMENDED.filter((r) => r.joinAt).map((r) => setTimeout(() => setInvited((prev) => [...prev, r.id]), r.joinAt!));
    return () => timers.forEach(clearTimeout);
  }, []);

  const joined = RECOMMENDED.filter((r) => invited.includes(r.id));
  const filled = 1 + joined.length; // host + accepted teammates

  return (
    <Stack gap={2.5} p={2.5} borderRadius="xl" bg="brand.700" border="1px solid" borderColor="whiteAlpha.100">
      <StartsInRow />

      {/* members - starts with only the host, fills as invites are accepted */}
      <Flex alignItems="center" justifyContent="space-between">
        <Box display="flex" pl="7px">
          <Box ml="-7px">
            <Avatar color={APEX} initial="R" size={26} />
          </Box>
          {joined.map((r) => (
            <Box key={r.id} ml="-7px">
              <Avatar color={r.color} initial={r.initial} size={26} delay={0} />
            </Box>
          ))}
          {['s1', 's2', 's3'].slice(0, Math.max(TEAM_LIMIT - filled, 0)).map((k) => (
            <Flex key={k} ml="-7px" w="26px" h="26px" borderRadius="full" border="1.5px dashed" borderColor="whiteAlpha.300" alignItems="center" justifyContent="center" color="whiteAlpha.400" fontSize="14px" bg="brand.700">
              +
            </Flex>
          ))}
        </Box>
        <Flex alignItems="center" gap={1} bgColor="bg.muted" borderRadius="full" px={2} py={0.5} fontSize="11px" fontWeight="700" color="fg.emphasized">
          <Box fontSize="12px">
            <PeopleIcon />
          </Box>
          {filled}/{TEAM_LIMIT}
        </Flex>
      </Flex>

      {/* recommended -> invite adds to squad, button flips to "In squad" */}
      <Stack gap={1.5} pt={1} borderTop="1px solid" borderColor="whiteAlpha.100">
        <Text fontSize="9px" fontWeight="700" color="fg.muted" textTransform="uppercase" letterSpacing="0.08em">
          Recommended
        </Text>
        {RECOMMENDED.map((r) => {
          const isInvited = invited.includes(r.id);
          return (
            <Flex key={r.id} alignItems="center" gap={2}>
              <Avatar color={r.color} initial={r.initial} size={22} />
              <Box flex={1} minW={0}>
                <Text fontSize="11px" fontWeight="700" color="fg.emphasized" lineHeight="1.1">
                  {r.name}
                </Text>
                <Text fontSize="9px" color="fg.muted" lineHeight="1.1">
                  {r.meta}
                </Text>
              </Box>
              {isInvited ? (
                <Flex alignItems="center" gap={1} px={2} py={1} borderRadius="lg" bg="whiteAlpha.100" color="#22c55e" fontSize="10px" fontWeight="700">
                  <CheckSvg size={10} />
                  In squad
                </Flex>
              ) : (
                <Flex px={2.5} py={1} borderRadius="lg" bg="purple.500" color="white" fontSize="10px" fontWeight="700">
                  Invite
                </Flex>
              )}
            </Flex>
          );
        })}
      </Stack>

      {/* voice - configured on the published post */}
      <Flex alignItems="center" gap={2} p={2} borderRadius="lg" bg="bg.muted">
        <Box color="purple.300" fontSize="15px">
          <MicOnIcon />
        </Box>
        <Text fontSize="11px" fontWeight="700" color="fg.emphasized" whiteSpace="nowrap">
          Teamplay voice
        </Text>
      </Flex>
    </Stack>
  );
};

const PostScreen = ({ game, color }: { game?: GameInfo; color: string }) => (
  <Box position="relative" h="100%" overflow="hidden">
    {/* "Post created" banner at the very top */}
    <Box position="absolute" top="30px" left={3} right={3} zIndex={10} asChild>
      <m.div initial={{ opacity: 0, y: -24 }} animate={{ opacity: [0, 1, 1, 0], y: [-24, 0, 0, -12] }} transition={{ delay: 0.2, duration: 2.6, times: [0, 0.12, 0.8, 1] }}>
        <Flex alignItems="center" gap={2} px={3} py={2} borderRadius="xl" bg="hsl(228,11.6%,15%)" border="1px solid" borderColor="whiteAlpha.200" style={{ boxShadow: '0 12px 34px -8px rgba(0,0,0,0.7)' }}>
          <Flex w="18px" h="18px" borderRadius="full" bg="#22c55e" alignItems="center" justifyContent="center" color="white">
            <CheckSvg />
          </Flex>
          <Text fontSize="12px" fontWeight="700" color="fg.emphasized">
            Post created
          </Text>
        </Flex>
      </m.div>
    </Box>

    <Box asChild>
      <m.div initial={{ y: 0 }} animate={{ y: [0, 0, -84, -84] }} transition={{ duration: 7, times: [0, 0.38, 0.85, 1], ease: 'easeInOut' }}>
        <Box>
          {/* game photo */}
          <Box position="relative" w="100%" h="130px" overflow="hidden">
            {game?.image ? (
              <Image src={game.image} alt="game" width={480} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
            ) : (
              <Box w="100%" h="100%" style={{ background: `linear-gradient(135deg, ${color} 0%, #1a1b21 90%)` }} />
            )}
            <Box position="absolute" inset={0} background="linear-gradient(to bottom, hsla(228,11.6%,7%,0.35) 0%, transparent 40%, hsla(228,11.6%,7%,0.9) 92%, hsl(228,11.6%,7%) 100%)" />
            <Flex position="absolute" top="40px" left={3} alignItems="center" gap={2} bg="blackAlpha.500" backdropFilter="blur(4px)" px={2.5} py={1} borderRadius="full">
              <GameIcon game={game} color={game?.iconColor || color} size={16} />
              <Text fontSize="11px" fontWeight="700" color="white">
                {game?.name ?? 'Apex Legends'}
                <Text as="span" fontWeight="400" color="whiteAlpha.700" ml={1}>
                  PC
                </Text>
              </Text>
            </Flex>
          </Box>

          <Flex direction="column" px={3.5} pb={3} gap={2.5} mt="-22px" position="relative">
            <Flex alignItems="center" gap={2.5}>
              <Avatar color={color} initial="R" size={46} border="hsl(228,11.6%,7%)" />
              <Stack gap={0.5} flex={1} minW={0}>
                <Heading fontSize="16px" display="flex" alignItems="center" gap={1.5} color="fg.emphasized" lineHeight="1">
                  Riftwalker 🇬🇧
                </Heading>
                <HStack gap={1} fontSize="11px" color="fg.muted">
                  <Text>22 years</Text>
                  <Text>&middot;</Text>
                  <Flex fontSize="13px" alignItems="center">
                    <MicOnIcon />
                  </Flex>
                  <Text>&middot;</Text>
                  <Text>English</Text>
                </HStack>
              </Stack>
            </Flex>

            <Box bg="bg.muted" borderRadius="lg" px={3} py={2}>
              <Text fontSize="12px" color="fg.emphasized" lineHeight="1.5">
                LFG for ranked. Chill vibes, mic on - let&apos;s climb together.
              </Text>
            </Box>

            <HStack gap={1.5}>
              <Tag>Diamond</Tag>
              <Tag>Ranked</Tag>
              <Tag>🌍 EU</Tag>
            </HStack>

            <SessionBlock />

            <Flex alignItems="center" justifyContent="space-between" fontSize="11px" color="fg.muted">
              <Text>updated just now</Text>
              <Flex alignItems="center" gap={1}>
                <Box fontSize="12px">
                  <DurationIcon />
                </Box>
                <Text>Next bump in 4h</Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </m.div>
    </Box>
  </Box>
);

export const PublishedPostMock = ({
  game,
  games,
  featuredSlug = 'apex-legends',
}: {
  game?: GameInfo;
  games?: GameInfo[];
  featuredSlug?: string;
}) => {
  const [cycle, setCycle] = useState(0);
  const [step, setStep] = useState<'game' | 'details' | 'post'>('game');
  const color = game?.iconColor || APEX;
  const pickerGames = games ?? [];
  const hasPicker = pickerGames.length >= 2;

  useEffect(() => {
    setStep(hasPicker ? 'game' : 'details');
    const t1 = hasPicker ? setTimeout(() => setStep('details'), GAME_MS) : undefined;
    const t2 = setTimeout(() => setStep('post'), CREATE_MS);
    const loop = setTimeout(() => setCycle((c) => c + 1), CYCLE_MS);
    return () => {
      if (t1) clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(loop);
    };
  }, [cycle, hasPicker]);

  const screen =
    step === 'game' ? (
      <GameScreen games={pickerGames} featuredSlug={featuredSlug} />
    ) : step === 'details' ? (
      <CreateScreen game={game} color={color} />
    ) : (
      <PostScreen game={game} color={color} />
    );

  return (
    <PhoneFrame>
      <Box position="relative" w="100%" h="100%">
        <AnimatePresence mode="sync">
          <Box key={`${step}${cycle}`} position="absolute" inset={0} asChild>
            <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
              {screen}
            </m.div>
          </Box>
        </AnimatePresence>
      </Box>
    </PhoneFrame>
  );
};
